import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import PuzzleCropModal from '../components/PuzzleCropModal';
import {
  Database,
  Mail,
  RefreshCw,
  Send,
  Eye,
  Users,
  Clock,
  Gauge,
  Gift,
  Copy,
  Video,
  Trash2,
  Camera,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH = { withCredentials: true };

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [bankStatus, setBankStatus] = useState(null);
  const [digestStatus, setDigestStatus] = useState(null);
  const [newsletter, setNewsletter] = useState(null);
  const [promoCodes, setPromoCodes] = useState(null);
  const [busy, setBusy] = useState({});
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSummary, setPreviewSummary] = useState(null);
  // Hand-curated puzzle submission (Admin: paste an exemplar puzzle, e.g.
  // an NYT classic, into the bank).
  const [curatedGrid, setCuratedGrid] = useState('');
  const [curatedSource, setCuratedSource] = useState('');
  const [curatedExpected, setCuratedExpected] = useState('');
  const [curatedResult, setCuratedResult] = useState(null);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrInfo, setOcrInfo] = useState(null); // { confidence, message } | null
  // Crop flow: when the user picks a photo, we open the crop modal first
  // (lets them frame the puzzle tight, which dramatically improves OCR).
  // Once they confirm, the cropped Blob is uploaded to the OCR endpoint.
  const [pendingCropFile, setPendingCropFile] = useState(null);

  // Lessons (YouTube-paired curated puzzles).
  const [lessons, setLessons] = useState([]);
  const [lessonGrid, setLessonGrid] = useState('');
  const [lessonTitle, setLessonTitle] = useState('');
  const [lessonDescription, setLessonDescription] = useState('');
  const [lessonVideoUrl, setLessonVideoUrl] = useState('');
  const [lessonCode, setLessonCode] = useState('');
  const [lessonLabel, setLessonLabel] = useState('');
  const [lessonTags, setLessonTags] = useState('');
  const [lessonIsomorphCount, setLessonIsomorphCount] = useState(24);

  // Flagged puzzles (per-puzzle thumbs-down aggregation).
  const [flagged, setFlagged] = useState(null);
  const [reviewing, setReviewing] = useState(null);

  const reviewFlag = async (key, action) => {
    setReviewing(`${key}:${action}`);
    try {
      await axios.post(`${API_URL}/api/admin/flagged-puzzles/${encodeURIComponent(key)}/review`, { action }, AUTH);
      setFlagged((f) => ({ ...f, flagged: f.flagged.filter((x) => x.puzzle_id !== key) }));
      window.dispatchEvent(new Event('flag-queue-changed'));
      toast.success(action === 'retire' ? 'Puzzle retired from rotation' : 'Flags dismissed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Review failed');
    } finally {
      setReviewing(null);
    }
  };

  // Per-puzzle reception telemetry (Phase 2).
  const [metrics, setMetrics] = useState(null);
  const [metricsSort, setMetricsSort] = useState('desirability');

  // Banked puzzles browser (admin can see what they've uploaded).
  const [banked, setBanked] = useState(null);
  const [bankedFilter, setBankedFilter] = useState('admin'); // 'admin' | 'all'
  const [bankedLevel, setBankedLevel] = useState(''); // '' = any level
  const [bankedExpanded, setBankedExpanded] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const loadAll = async () => {
    // Use allSettled so a single failing endpoint doesn't leave every card
    // stuck on "Loading…". The global axios 401 interceptor (in AuthContext)
    // transparently refreshes the access token and retries each request
    // once — so any 401 we see HERE is a real auth failure, not a stale
    // cookie.
    const bankedParams = new URLSearchParams();
    if (bankedFilter === 'admin') bankedParams.set('admin_only', 'true');
    if (bankedLevel) bankedParams.set('level', bankedLevel);
    bankedParams.set('limit', '50');

    const [banks, digest, subs, promos, lessonsResp, flagsResp, metricsResp, bankedResp] = await Promise.allSettled([
      axios.get(`${API_URL}/api/admin/bank-status`, AUTH),
      axios.get(`${API_URL}/api/admin/weekly-digest/status`, AUTH),
      axios.get(`${API_URL}/api/admin/newsletter`, AUTH),
      axios.get(`${API_URL}/api/admin/promo-codes`, AUTH),
      axios.get(`${API_URL}/api/lessons?limit=100`),
      axios.get(`${API_URL}/api/admin/flagged-puzzles?limit=50`, AUTH),
      axios.get(`${API_URL}/api/admin/puzzle-metrics?limit=30&sort=${metricsSort}`, AUTH),
      axios.get(`${API_URL}/api/admin/banked-puzzles?${bankedParams.toString()}`, AUTH),
    ]);

    setBankStatus(banks.status === 'fulfilled' ? banks.value.data : { _error: banks.reason?.message || 'Failed to load' });
    setDigestStatus(digest.status === 'fulfilled' ? digest.value.data : { _error: digest.reason?.message || 'Failed to load' });
    setNewsletter(subs.status === 'fulfilled' ? subs.value.data : { _error: subs.reason?.message || 'Failed to load' });
    setPromoCodes(promos.status === 'fulfilled' ? promos.value.data : { _error: promos.reason?.message || 'Failed to load' });
    setLessons(lessonsResp.status === 'fulfilled' ? (lessonsResp.value.data?.items || []) : []);
    setFlagged(flagsResp.status === 'fulfilled' ? flagsResp.value.data : { _error: flagsResp.reason?.message || 'Failed to load' });
    setMetrics(metricsResp.status === 'fulfilled' ? metricsResp.value.data : { _error: metricsResp.reason?.message || 'Failed to load' });
    setBanked(bankedResp.status === 'fulfilled' ? bankedResp.value.data : { _error: bankedResp.reason?.message || 'Failed to load' });

    const hardAuthFail = [banks, digest, subs, promos].some(
      (r) => r.status === 'rejected' && r.reason?.response?.status === 401,
    );
    if (hardAuthFail) {
      toast.error('Your session expired — please sign in again');
      navigate('/login');
      return;
    }

    const firstFailure = [banks, digest, subs, promos].find((r) => r.status === 'rejected');
    if (firstFailure) {
      const status = firstFailure.reason?.response?.status;
      const detail = firstFailure.reason?.response?.data?.detail;
      toast.error(`Some admin data failed to load${status ? ` (HTTP ${status})` : ''}${detail ? `: ${detail}` : ''}`);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch {
      toast.error('Copy failed — select the code manually');
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') loadAll();
  }, [user, metricsSort, bankedFilter, bankedLevel]);

  const farmBank = async (level) => {
    const key = `farm-${level}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/farm-bank/${level}`,
        null,
        AUTH
      );
      toast.success(data.message || `Farming L${level} started`);
      // Wait briefly so the farmer has time to insert at least one
      // puzzle, then refresh. Inline await — keeps lint happy and
      // ensures setState calls happen in the user-action handler
      // path, not via setTimeout.
      await new Promise((r) => setTimeout(r, 1500));
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Farm request failed');
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const previewDigest = async () => {
    setBusy((b) => ({ ...b, preview: true }));
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/weekly-digest/preview`,
        AUTH
      );
      setPreviewHtml(data.html);
      setPreviewSummary(data.summary);
    } catch (e) {
      toast.error('Preview failed');
    } finally {
      setBusy((b) => ({ ...b, preview: false }));
    }
  };

  const sendDigest = async () => {
    if (!window.confirm(
      `Send weekly digest to ${newsletter?.count ?? '?'} subscribers?\n` +
      `This cannot be undone.`
    )) return;
    setBusy((b) => ({ ...b, send: true }));
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/weekly-digest/send`,
        null,
        AUTH
      );
      toast.success(`Sent to ${data.sent} subscribers (${data.failed} failed)`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setBusy((b) => ({ ...b, send: false }));
    }
  };

  const submitCuratedPuzzle = async () => {
    setBusy((b) => ({ ...b, curate: true }));
    setCuratedResult(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/banked-puzzles`,
        {
          grid: curatedGrid,
          source: curatedSource || null,
          expected_level: curatedExpected ? Number(curatedExpected) : null,
        },
        AUTH,
      );
      setCuratedResult(data);
      toast.success(`Filed at L${data.level} — ${data.difficulty_name}`);
      // Refresh banks card so the user sees the new count.
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally {
      setBusy((b) => ({ ...b, curate: false }));
    }
  };

  const deflateCuratedPuzzle = async () => {
    setBusy((b) => ({ ...b, deflate: true }));
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/deflate-puzzle`,
        { grid: curatedGrid, targets: [1, 2, 3, 4], attempts: 5 },
        AUTH,
      );
      const ok = (data.results || []).filter((r) => r.ok);
      toast.success(
        `Deflated L${data.source_level} → ${ok.length} variant${ok.length === 1 ? '' : 's'} filed in L1–L4`,
      );
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Deflation failed');
    } finally {
      setBusy((b) => ({ ...b, deflate: false }));
    }
  };

  // Photo OCR: client downsamples + base64-encodes the user's image,
  // POSTs to /api/admin/puzzle-ocr (gpt-4o vision), drops the 81-digit
  // grid into the textarea pre-formatted into 9 rows for review.
  const handlePuzzlePhoto = (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.error('Use a JPEG, PNG, or WEBP image.');
      return;
    }
    // Open the crop modal first; OCR happens after the user confirms a crop.
    setPendingCropFile(file);
  };

  // Called when the crop modal returns a cropped JPEG Blob.
  const handleCroppedBlob = async (blob) => {
    setPendingCropFile(null);
    if (!blob) return;
    setOcrBusy(true);
    setOcrInfo(null);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result || '';
          resolve(String(result).split(',')[1] || '');
        };
        reader.onerror = () => reject(new Error('file-read-failed'));
        reader.readAsDataURL(blob);
      });

      const { data } = await axios.post(
        `${API_URL}/api/admin/puzzle-ocr`,
        { image_base64: base64, mime_type: 'image/jpeg' },
        AUTH,
      );
      if (!data.ok || !data.grid) {
        setOcrInfo({ confidence: data.confidence || 'low', message: data.message || 'OCR failed.' });
        toast.error(data.message || 'Could not read a sudoku grid from that photo. Try a tighter crop on just the 9x9 grid.');
        return;
      }
      // Format 81 digits into 9 rows of 3-3-3 groups for easy proofreading.
      const g = data.grid;
      const rows = [];
      for (let r = 0; r < 9; r++) {
        const row = g.slice(r * 9, r * 9 + 9);
        rows.push(`${row.slice(0, 3)} ${row.slice(3, 6)} ${row.slice(6, 9)}`);
      }
      setCuratedGrid(rows.join('\n'));
      setOcrInfo({ confidence: data.confidence, message: data.message });
      const confLabel = data.confidence === 'high' ? 'High confidence' : data.confidence === 'medium' ? 'Medium confidence' : 'Low confidence';
      toast.success(`${confLabel} — review carefully before submitting.`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Failed to process image';
      toast.error(detail);
    } finally {
      setOcrBusy(false);
    }
  };

  const createLesson = async () => {
    if (!lessonGrid.trim() || !lessonTitle.trim()) {
      toast.error('Grid and title are required');
      return;
    }
    setBusy((b) => ({ ...b, lesson: true }));
    try {
      const tagList = lessonTags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const { data } = await axios.post(
        `${API_URL}/api/admin/lessons`,
        {
          grid: lessonGrid,
          title: lessonTitle,
          description: lessonDescription || '',
          video_url: lessonVideoUrl || null,
          code: lessonCode || null,
          label: lessonLabel || null,
          tags: tagList,
          isomorph_count: Number(lessonIsomorphCount) || 0,
        },
        AUTH,
      );
      const banked = data.isomorphs_banked || 0;
      toast.success(
        `Created ${data.code} — L${data.difficulty_level}${
          banked ? ` (+${banked} isomorphs banked)` : ''
        }`,
      );
      setLessonGrid('');
      setLessonTitle('');
      setLessonDescription('');
      setLessonVideoUrl('');
      setLessonCode('');
      setLessonLabel('');
      setLessonTags('');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lesson creation failed');
    } finally {
      setBusy((b) => ({ ...b, lesson: false }));
    }
  };

  const deleteLesson = async (code) => {
    if (!window.confirm(`Delete lesson ${code}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/lessons/${encodeURIComponent(code)}`, AUTH);
      toast.success(`Deleted ${code}`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (authLoading || !user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-main)' }}>
        <div className="w-12 h-12 border-4 border-[#8B5A3C]/30 border-t-[#8B5A3C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[var(--text-primary)]">
              Admin
            </h1>
            <p className="text-[var(--text-secondary)] text-sm">
              Operations console — banks, digests, subscribers.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadAll}
            className="border-[var(--border-subtle)]"
            data-testid="admin-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Puzzle bank card */}
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]" data-testid="card-banks">
            <CardHeader>
              <CardTitle className="text-[var(--text-primary)] flex items-center gap-2 font-heading">
                <Database className="w-4 h-4 text-[#8B5A3C]" />
                Puzzle Banks
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!bankStatus ? (
                <div className="text-sm text-[var(--text-disabled)]">Loading…</div>
              ) : bankStatus._error ? (
                <div className="text-sm text-[#FF453A]" data-testid="bank-error">
                  Failed to load banks: {bankStatus._error}
                </div>
              ) : (
                bankStatus.levels.map((lvl) => {
                  const pct = Math.min(100, (lvl.count / bankStatus.target_per_level) * 100);
                  const full = lvl.count >= bankStatus.target_per_level;
                  return (
                    <div key={lvl.level} data-testid={`bank-l${lvl.level}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-[var(--text-primary)] font-medium">
                          L{lvl.level}{' '}
                          <span className="text-[var(--text-disabled)] font-mono text-xs">
                            {lvl.count}/{bankStatus.target_per_level}
                          </span>
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => farmBank(lvl.level)}
                          disabled={busy[`farm-${lvl.level}`] || full}
                          className="h-7 text-xs"
                          data-testid={`farm-btn-l${lvl.level}`}
                        >
                          {full ? 'Full' : busy[`farm-${lvl.level}`] ? 'Starting…' : 'Farm now'}
                        </Button>
                      </div>
                      <div className="h-1.5 bg-[var(--hover-bg-subtle)] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: full ? '#34C759' : '#8B5A3C',
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {/* Newsletter card */}
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]" data-testid="card-newsletter">
            <CardHeader>
              <CardTitle className="text-[var(--text-primary)] flex items-center gap-2 font-heading">
                <Users className="w-4 h-4 text-[#34C759]" />
                Newsletter
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-heading font-bold text-[var(--text-primary)] mb-1" data-testid="newsletter-count">
                {newsletter?.count ?? '—'}
              </div>
              <div className="text-sm text-[var(--text-secondary)]">Subscribers</div>
              {newsletter?.subscribers?.[0] && (
                <div className="mt-4 text-xs text-[var(--text-disabled)] font-mono">
                  Latest: {newsletter.subscribers[0].email}
                  <br />
                  {new Date(newsletter.subscribers[0].subscribed_at).toLocaleString()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Promo codes card — spans full width */}
          <Card
            className="bg-[var(--background-surface)] border-[var(--border-subtle)] md:col-span-2"
            data-testid="card-promo-codes"
          >
            <CardHeader>
              <CardTitle className="text-[var(--text-primary)] flex items-center gap-2 font-heading">
                <Gift className="w-4 h-4 text-[#FF9F0A]" />
                Promo Codes
                {promoCodes && !promoCodes._error && Array.isArray(promoCodes) && (
                  <span className="ml-2 text-xs font-mono text-[var(--text-disabled)]">
                    {promoCodes.filter((c) => !c.redeemed_by).length} unredeemed / {promoCodes.length} total
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!promoCodes ? (
                <div className="text-sm text-[var(--text-disabled)]">Loading…</div>
              ) : promoCodes._error ? (
                <div className="text-sm text-[#FF453A]" data-testid="promo-error">
                  Failed to load promo codes: {promoCodes._error}
                </div>
              ) : promoCodes.filter((c) => !c.redeemed_by).length === 0 ? (
                <div className="text-sm text-[var(--text-disabled)]">
                  All codes have been redeemed.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="promo-codes-grid">
                  {promoCodes
                    .filter((c) => !c.redeemed_by)
                    .map((c) => (
                      <div
                        key={c.code}
                        className="flex items-center justify-between gap-2 px-3 py-2 rounded border border-[var(--border-subtle)] bg-[var(--hover-bg-subtle)]"
                        data-testid={`promo-code-row-${c.code}`}
                      >
                        <div className="min-w-0">
                          <div className="font-mono text-sm text-[var(--text-primary)] truncate" title={c.code}>
                            {c.code}
                          </div>
                          <div className="text-[10px] text-[var(--text-disabled)]">
                            {c.tier} · {c.duration_days} days
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyCode(c.code)}
                          className="h-7 w-7 p-0 shrink-0"
                          data-testid={`promo-copy-${c.code}`}
                          title="Copy to clipboard"
                          aria-label={`Copy code ${c.code}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                </div>
              )}
              <p className="mt-4 text-xs text-[var(--text-disabled)]">
                Single-use. Each code upgrades one account to premium for its duration. Share carefully — anyone with the code can redeem it.
              </p>
            </CardContent>
          </Card>

          {/* Flagged puzzles — community reception (thumbs-down count) */}
          <Card
            className="bg-[var(--background-surface)] border-[var(--border-subtle)] md:col-span-2"
            data-testid="card-flagged-puzzles"
          >
            <CardHeader>
              <CardTitle className="text-[var(--text-primary)] flex items-center gap-2 font-heading">
                <Trash2 className="w-4 h-4 text-[#FF453A]" />
                Flagged Puzzles
                {flagged && !flagged._error && Array.isArray(flagged.flagged) && (
                  <span className="ml-2 text-xs font-mono text-[var(--text-disabled)]">
                    {flagged.flagged.length} puzzles flagged
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!flagged ? (
                <div className="text-sm text-[var(--text-disabled)]">Loading…</div>
              ) : flagged._error ? (
                <div className="text-sm text-[#FF453A]" data-testid="flagged-error">
                  Failed to load flagged puzzles: {flagged._error}
                </div>
              ) : !flagged.flagged || flagged.flagged.length === 0 ? (
                <div className="text-sm text-[var(--text-disabled)]" data-testid="flagged-empty">
                  No puzzles have been flagged yet. The community has spoken &mdash; everything is loved.
                </div>
              ) : (
                <div className="space-y-2" data-testid="flagged-list">
                  {flagged.flagged.map((f) => (
                    <div
                      key={f.puzzle_id}
                      className="flex items-start justify-between gap-3 px-3 py-2 rounded border border-[var(--border-subtle)] bg-[var(--hover-bg-subtle)]"
                      data-testid={`flagged-row-${f.puzzle_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-[var(--text-primary)] truncate" title={f.puzzle_id}>
                            {f.puzzle_id.slice(0, 14)}…
                          </span>
                          {f.level && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8B5A3C]/15 text-[#A46944] border border-[#8B5A3C]/30">
                              L{f.level}
                            </span>
                          )}
                          <span className="text-[10px] text-[var(--text-disabled)]">
                            {f.flag_count} flag{f.flag_count === 1 ? '' : 's'}
                          </span>
                          {f.auto_retired && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF453A]/15 text-[#FF453A] border border-[#FF453A]/30"
                              data-testid={`flagged-auto-retired-${f.puzzle_id}`}
                            >
                              Auto-retired
                            </span>
                          )}
                        </div>
                        {f.recent_reasons.length > 0 && (
                          <ul className="mt-1 ml-3 list-disc text-[11px] text-[var(--text-secondary)] space-y-0.5">
                            {f.recent_reasons.map((r, i) => (
                              <li key={i} className="line-clamp-2">{r}</li>
                            ))}
                          </ul>
                        )}
                        {(f.help_requests || []).map((h) => (
                          <div key={h.id} className="mt-2 flex gap-3 rounded-lg border border-[var(--border-subtle)] p-2" data-testid={`flag-help-particulars-${h.id}`}>
                            <a href={`${API_URL}${h.image_url}`} target="_blank" rel="noreferrer" className="shrink-0">
                              <img src={`${API_URL}${h.image_url}`} alt="Player grid snapshot" className="w-24 h-24 rounded object-contain bg-[#FFF3E4]" loading="lazy" />
                            </a>
                            <div className="min-w-0 text-[11px] text-[var(--text-secondary)] space-y-0.5">
                              <p><span className="font-semibold text-[var(--text-primary)]">{h.display_name}</span> was still stuck after validator, check-work, solver and community.</p>
                              <p>{h.wrong_count} wrong entries · {h.community_replies} community {h.community_replies === 1 ? 'reply' : 'replies'}</p>
                              {h.note && <p className="italic line-clamp-2">&ldquo;{h.note}&rdquo;</p>}
                              {h.last_replies.map((r, i) => (
                                <p key={i} className="line-clamp-2"><span className="font-medium">{r.from_player ? 'Player' : r.author}:</span> {r.content}</p>
                              ))}
                              {h.thread_url && (
                                <a href={h.thread_url} target="_blank" rel="noreferrer" className="text-[#8B5A3C] underline" data-testid={`flag-help-thread-link-${h.id}`}>Open Discord thread</a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!!reviewing}
                          onClick={() => reviewFlag(f.puzzle_id, 'dismiss')}
                          className="h-7 text-xs border-[var(--border-subtle)] text-[var(--text-secondary)]"
                          data-testid={`flag-dismiss-btn-${f.puzzle_id}`}
                        >
                          {f.auto_retired ? 'Restore' : 'Dismiss'}
                        </Button>
                        <Button
                          size="sm"
                          disabled={!!reviewing}
                          onClick={() => reviewFlag(f.puzzle_id, 'retire')}
                          className="h-7 text-xs bg-[#FF453A] hover:bg-[#E03A30] text-white"
                          data-testid={`flag-retire-btn-${f.puzzle_id}`}
                        >
                          Retire
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-4 text-xs text-[var(--text-disabled)]">
                Each row is one puzzle awaiting review. Players reach this queue only after the validator, check-work, solver and community thread could not get them unstuck; their grid snapshot and thread are attached. Dismiss keeps the puzzle in rotation; Retire removes it for good.
              </p>
            </CardContent>
          </Card>

          {/* Banked Puzzles — what you've uploaded */}
          <Card
            className="bg-[var(--background-surface)] border-[var(--border-subtle)] md:col-span-2"
            data-testid="card-banked-puzzles"
          >
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setBankedExpanded((v) => !v)}
                  className="flex items-center gap-2 text-left group"
                  data-testid="banked-toggle"
                  aria-expanded={bankedExpanded}
                >
                  <CardTitle className="text-[var(--text-primary)] flex items-center gap-2 font-heading">
                    <Database className="w-4 h-4 text-[#A46944]" />
                    Banked Puzzles
                    {banked && !banked._error && (
                      <span className="ml-2 text-xs font-mono text-[var(--text-disabled)]">
                        {banked.total_in_set} {banked.total_in_set === 1 ? 'puzzle' : 'puzzles'}
                      </span>
                    )}
                    <span
                      className="text-xs text-[var(--text-disabled)] group-hover:text-[var(--text-secondary)] transition-colors"
                      aria-hidden="true"
                    >
                      {bankedExpanded ? '▾ hide' : '▸ show'}
                    </span>
                  </CardTitle>
                </button>
                {bankedExpanded && (
                  <div className="flex gap-1.5 flex-wrap">
                    <button
                      onClick={() => setBankedFilter('admin')}
                      className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                        bankedFilter === 'admin'
                          ? 'bg-[var(--accent-primary)] text-white'
                          : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
                      }`}
                      data-testid="banked-filter-admin"
                    >
                      Your uploads
                    </button>
                    <button
                      onClick={() => setBankedFilter('all')}
                      className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                        bankedFilter === 'all'
                          ? 'bg-[var(--accent-primary)] text-white'
                          : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
                      }`}
                      data-testid="banked-filter-all"
                    >
                      All puzzles
                    </button>
                    <select
                      value={bankedLevel}
                      onChange={(e) => setBankedLevel(e.target.value)}
                      className="px-2 py-1 rounded text-[11px] bg-[var(--hover-bg-subtle)] text-[var(--text-secondary)] border border-[var(--border-subtle)]"
                      data-testid="banked-level-filter"
                    >
                      <option value="">All levels</option>
                      {Array.from({ length: 17 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>{`L${n}`}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </CardHeader>
            {bankedExpanded && (
            <CardContent>
              {!banked ? (
                <div className="text-sm text-[var(--text-disabled)]">Loading…</div>
              ) : banked._error ? (
                <div className="text-sm text-[#FF453A]" data-testid="banked-error">
                  Failed to load: {banked._error}
                </div>
              ) : !banked.puzzles || banked.puzzles.length === 0 ? (
                <div className="text-sm text-[var(--text-disabled)]" data-testid="banked-empty">
                  {bankedFilter === 'admin'
                    ? "You haven't uploaded any puzzles yet. Use the upload card above to add one — it'll appear here."
                    : 'No puzzles in the bank matching this filter.'}
                </div>
              ) : (
                <div
                  className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                  data-testid="banked-grid"
                >
                  {banked.puzzles.map((p, idx) => {
                    const compact = p.grid_compact || '';
                    const cells = compact ? compact.split('') : [];
                    return (
                      <div
                        key={`${p.essential_puzzle_id || idx}`}
                        className="bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] rounded p-2 text-xs"
                        data-testid={`banked-card-${p.essential_puzzle_id || idx}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8B5A3C]/15 text-[#A46944] border border-[#8B5A3C]/30">
                              L{p.level}
                            </span>
                            {p.admin_uploaded && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                                yours
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] text-[var(--text-disabled)] font-mono">
                            {p.cells_given} clues
                          </span>
                        </div>
                        {/* Mini grid thumbnail */}
                        <div
                          className="grid grid-cols-9 gap-px bg-[var(--border-medium)] p-px rounded mb-2"
                          style={{ aspectRatio: '1' }}
                        >
                          {cells.map((c, i) => (
                            <div
                              key={i}
                              className={`flex items-center justify-center bg-[var(--background-surface)] text-[8px] font-mono ${
                                c === '0' ? 'text-[var(--text-disabled)]' : 'text-[var(--text-primary)] font-semibold'
                              }`}
                            >
                              {c === '0' ? '' : c}
                            </div>
                          ))}
                        </div>
                        <div className="text-[10px] text-[var(--text-secondary)] truncate" title={p.source || ''}>
                          {p.source || <span className="text-[var(--text-disabled)]">no source</span>}
                        </div>
                        <div className="text-[10px] text-[var(--text-disabled)] mt-0.5">
                          #{p.series_index}/{p.series_total} · score {p.complexity_score ?? '—'}
                        </div>
                        {/* Lineage — ↑ parent provenance and ↓ deflation children */}
                        {(p.deflated_from_essential_id || (p.children && p.children.length > 0)) && (
                          <div
                            className="mt-1.5 pt-1.5 border-t border-[var(--border-subtle)] text-[10px] space-y-0.5"
                            data-testid={`banked-lineage-${p.essential_puzzle_id || idx}`}
                          >
                            {p.deflated_from_essential_id && (
                              <div
                                className="text-[var(--text-disabled)] truncate"
                                title={`deflated from essential ${p.deflated_from_essential_id} (added ${p.clues_injected ?? '?'} clues)`}
                              >
                                <span className="text-amber-400">↑</span>{' '}
                                from L{p.deflated_from_level ?? '?'} (+
                                {p.clues_injected ?? '?'} clues)
                              </div>
                            )}
                            {p.children && p.children.length > 0 && (
                              <div
                                className="text-emerald-400 truncate"
                                title={p.children
                                  .map((c) => `L${c.level} · +${c.clues_injected ?? '?'}`)
                                  .join('\n')}
                                data-testid={`banked-children-${p.essential_puzzle_id || idx}`}
                              >
                                <span>↓</span> {p.children.length} child
                                {p.children.length === 1 ? '' : 'ren'}: L
                                {p.children.map((c) => c.level).join(' · L')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="mt-4 text-xs text-[var(--text-disabled)]">
                Your uploads jump to the top of the served stack and auto-multiply into ~3.36M cosmetic variants (random isomorphism per fetch). Once a user solves an essential, the curriculum advances them.
              </p>
            </CardContent>
            )}
          </Card>

          {/* Per-puzzle reception telemetry (Phase 2) */}
          <Card
            className="bg-[var(--background-surface)] border-[var(--border-subtle)] md:col-span-2"
            data-testid="card-puzzle-metrics"
          >
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-[var(--text-primary)] flex items-center gap-2 font-heading">
                  <Gauge className="w-4 h-4 text-[#A46944]" />
                  Puzzle Reception (Phase 2)
                </CardTitle>
                <div className="flex gap-1.5">
                  {[
                    ['desirability', 'Best'],
                    ['abandon_rate', 'Most abandoned'],
                    ['flags', 'Most flagged'],
                    ['plays', 'Most plays'],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setMetricsSort(key)}
                      className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                        metricsSort === key
                          ? 'bg-[var(--accent-primary)] text-white'
                          : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
                      }`}
                      data-testid={`metrics-sort-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!metrics ? (
                <div className="text-sm text-[var(--text-disabled)]">Loading…</div>
              ) : metrics._error ? (
                <div className="text-sm text-[#FF453A]" data-testid="metrics-error">
                  Failed to load metrics: {metrics._error}
                </div>
              ) : !metrics.puzzles || metrics.puzzles.length === 0 ? (
                <div className="text-sm text-[var(--text-disabled)]" data-testid="metrics-empty">
                  No reception data yet — telemetry kicks in once users start completing or abandoning sessions.
                </div>
              ) : (
                <div className="overflow-x-auto" data-testid="metrics-table-wrap">
                  <table className="w-full text-xs" data-testid="metrics-table">
                    <thead>
                      <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-subtle)]">
                        <th className="py-2 pr-2">Puzzle</th>
                        <th className="py-2 px-2">L</th>
                        <th className="py-2 px-2 text-right">Plays</th>
                        <th className="py-2 px-2 text-right">Solve %</th>
                        <th className="py-2 px-2 text-right">Abandons</th>
                        <th className="py-2 px-2 text-right">Avg time</th>
                        <th className="py-2 px-2 text-right">Hints/solve</th>
                        <th className="py-2 px-2 text-right">Flags</th>
                        <th className="py-2 pl-2 text-right">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.puzzles.map((m) => {
                        const fmtTime = (s) => {
                          if (s == null) return '—';
                          const mins = Math.floor(s / 60);
                          const secs = Math.round(s % 60);
                          return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                        };
                        return (
                          <tr
                            key={m.puzzle_id}
                            className="border-b border-[var(--border-subtle)] last:border-0 text-[var(--text-secondary)]"
                            data-testid={`metrics-row-${m.puzzle_id}`}
                          >
                            <td className="py-1.5 pr-2 font-mono truncate max-w-[140px]" title={m.puzzle_id}>
                              {m.puzzle_id.slice(0, 12)}…
                            </td>
                            <td className="py-1.5 px-2">{m.difficulty_level ?? '—'}</td>
                            <td className="py-1.5 px-2 text-right">{m.sessions_started}</td>
                            <td className="py-1.5 px-2 text-right">{Math.round(m.solve_rate * 100)}%</td>
                            <td className="py-1.5 px-2 text-right">{m.abandoned}</td>
                            <td className="py-1.5 px-2 text-right">{fmtTime(m.avg_time_seconds)}</td>
                            <td className="py-1.5 px-2 text-right">{m.avg_hints_per_solve.toFixed(2)}</td>
                            <td className="py-1.5 px-2 text-right">
                              {m.flag_count > 0
                                ? <span className="text-[#FF453A]">{m.flag_count}</span>
                                : <span className="text-[var(--text-disabled)]">0</span>}
                            </td>
                            <td
                              className={`py-1.5 pl-2 text-right font-mono font-medium ${
                                m.desirability_score >= 0.7
                                  ? 'text-emerald-400'
                                  : m.desirability_score >= 0.5
                                  ? 'text-amber-400'
                                  : 'text-[#FF453A]'
                              }`}
                            >
                              {m.desirability_score.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="mt-4 text-xs text-[var(--text-disabled)]">
                Desirability = 0.50 × solve-rate + 0.20 × (1 − normalized-hints) + 0.30 × (1 − normalized-flags). Higher is better. Switch to <span className="font-mono">Most abandoned</span> or <span className="font-mono">Most flagged</span> to triage the worst.
              </p>
            </CardContent>
          </Card>

          {/* Curated puzzle submission — paste an exemplar (e.g. NYT
              classic) and our pipeline grades + multiplies + files it.
              Spans full width. */}
          <Card
            className="bg-[var(--background-surface)] border-[var(--border-subtle)] md:col-span-2"
            data-testid="card-curated-puzzle"
          >
            <CardHeader>
              <CardTitle className="text-[var(--text-primary)] flex items-center gap-2 font-heading">
                <Database className="w-4 h-4 text-[#5E5CE6]" />
                Submit Curated Puzzle
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-[var(--text-disabled)]">
                Paste an 81-digit grid — 9 digits per row, 9 rows (use 0 or . for empty cells).
                Spaces between 3-digit groups are optional; newlines and extra whitespace are
                ignored. We re-grade with our solver, attach curriculum metadata, and file it
                into the appropriate bank. Each served variant gets ~3.36M cosmetic transforms
                automatically.
              </p>
              {/* Photo OCR removed — manual entry only. */}
              <textarea
                value={curatedGrid}
                onChange={(e) => setCuratedGrid(e.target.value)}
                rows={9}
                spellCheck={false}
                autoCapitalize="none"
                autoCorrect="off"
                className="w-full font-mono text-sm bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] rounded p-2 text-[var(--text-primary)] leading-relaxed"
                placeholder={`530 070 000\n600 195 000\n098 000 060\n800 060 003\n400 803 001\n700 020 006\n060 000 280\n000 419 005\n000 080 079`}
                data-testid="curated-grid-input"
              />
              <p className="text-[10px] text-[var(--text-disabled)] -mt-1">
                Format: <span className="font-mono">3 digits · space · 3 digits · space · 3 digits</span> per row, 9 rows. Use <span className="font-mono">0</span> for empty cells.
              </p>
              {(() => {
                const digits = (curatedGrid.match(/[0-9.]/g) || []).length;
                if (digits === 0) return null;
                const ok = digits === 81;
                return (
                  <p
                    className={`text-xs font-mono ${ok ? 'text-emerald-400' : 'text-amber-400'}`}
                    data-testid="curated-digit-count"
                  >
                    {digits} / 81 digits {ok ? '✓' : digits > 81 ? `(remove ${digits - 81})` : `(need ${81 - digits} more)`}
                  </p>
                );
              })()}
              <div className="flex flex-wrap gap-2">
                <input
                  type="text"
                  value={curatedSource}
                  onChange={(e) => setCuratedSource(e.target.value)}
                  placeholder="Source (e.g. NYT 2024-12-04 Hard)"
                  className="flex-1 min-w-[200px] text-sm bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] rounded px-2 py-1.5 text-[var(--text-primary)]"
                  data-testid="curated-source-input"
                />
                <input
                  type="number"
                  min="1"
                  max="17"
                  value={curatedExpected}
                  onChange={(e) => setCuratedExpected(e.target.value)}
                  placeholder="Expected L (1-17)"
                  className="w-32 text-sm bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] rounded px-2 py-1.5 text-[var(--text-primary)]"
                  data-testid="curated-expected-input"
                />
                <Button
                  onClick={submitCuratedPuzzle}
                  disabled={busy.curate || !curatedGrid.trim()}
                  data-testid="curated-submit-btn"
                  className="bg-[#5E5CE6] hover:bg-[#5E5CE6]/90 text-white"
                >
                  {busy.curate ? 'Grading…' : 'Submit & file'}
                </Button>
              </div>
              {curatedResult && (
                <div
                  className="text-xs font-mono bg-[var(--hover-bg-subtle)] rounded p-3 border border-[var(--border-subtle)]"
                  data-testid="curated-result"
                >
                  <div className="text-[var(--text-primary)] font-semibold">
                    Filed at L{curatedResult.level} — {curatedResult.difficulty_name}
                  </div>
                  <div className="text-[var(--text-disabled)] mt-1">
                    cells_given: {curatedResult.cells_given} · complexity:{' '}
                    {curatedResult.complexity_score} · series_index:{' '}
                    {curatedResult.series_index}/{curatedResult.series_total}
                  </div>
                  <div className="text-[var(--text-disabled)] mt-1 break-all">
                    chain: {curatedResult.technique_chain.join(' → ')}
                  </div>
                  {curatedResult.expected_level !== null && curatedResult.expected_level !== undefined && (
                    <div
                      className={`mt-1 ${curatedResult.level_matches_expectation ? 'text-[#34C759]' : 'text-[#FF9F0A]'}`}
                    >
                      {curatedResult.level_matches_expectation
                        ? `✓ matches your expected L${curatedResult.expected_level}`
                        : `⚠ you guessed L${curatedResult.expected_level}, grader says L${curatedResult.level}`}
                    </div>
                  )}
                  {curatedResult.level >= 5 && (
                    <div className="mt-3 pt-3 border-t border-[var(--border-subtle)] flex flex-wrap items-center gap-2">
                      <span className="text-[var(--text-disabled)]">
                        Auto-deflate this puzzle to fill L1–L4 banks (greedy clue injection):
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={deflateCuratedPuzzle}
                        disabled={busy.deflate}
                        data-testid="curated-deflate-btn"
                        className="text-xs"
                      >
                        {busy.deflate ? 'Deflating…' : 'Deflate → L1–L4'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lessons (YouTube-paired) — full width */}
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] md:col-span-2" data-testid="card-lessons">
            <CardHeader>
              <CardTitle className="text-[var(--text-primary)] flex items-center gap-2 font-heading">
                <Video className="w-4 h-4 text-[#FF3B30]" />
                Video Lessons
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-[var(--text-secondary)]">
                Pair a YouTube video with a specific puzzle. Codes default to{' '}
                <code className="font-mono">LSN-YYMMDD</code> (with{' '}
                <code className="font-mono">-A</code>, <code className="font-mono">-B</code>,
                … suffixes for multiple lessons per day). Free users see the video and
                description; the play button is Premium-gated unless the puzzle is L≤4.
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <textarea
                  value={lessonGrid}
                  onChange={(e) => setLessonGrid(e.target.value)}
                  placeholder="Paste 81-char puzzle (digits + 0/. for empties)"
                  rows={6}
                  className="w-full font-mono text-xs bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] rounded p-3 text-[var(--text-primary)] placeholder:text-[var(--text-disabled)]"
                  data-testid="lesson-grid-input"
                />
                <div className="space-y-2">
                  <input
                    type="text"
                    value={lessonTitle}
                    onChange={(e) => setLessonTitle(e.target.value)}
                    placeholder="Lesson title (required)"
                    className="w-full text-sm bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] rounded px-3 py-2 text-[var(--text-primary)]"
                    data-testid="lesson-title-input"
                  />
                  <input
                    type="text"
                    value={lessonVideoUrl}
                    onChange={(e) => setLessonVideoUrl(e.target.value)}
                    placeholder="YouTube URL (any format)"
                    className="w-full text-sm bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] rounded px-3 py-2 text-[var(--text-primary)]"
                    data-testid="lesson-video-input"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={lessonCode}
                      onChange={(e) => setLessonCode(e.target.value)}
                      placeholder="Code (auto: LSN-YYMMDD)"
                      className="w-full font-mono text-sm bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] rounded px-3 py-2 text-[var(--text-primary)]"
                      data-testid="lesson-code-input"
                    />
                    <input
                      type="text"
                      value={lessonLabel}
                      onChange={(e) => setLessonLabel(e.target.value)}
                      placeholder="Label (optional)"
                      className="w-full text-sm bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] rounded px-3 py-2 text-[var(--text-primary)]"
                      data-testid="lesson-label-input"
                    />
                  </div>
                  <input
                    type="text"
                    value={lessonTags}
                    onChange={(e) => setLessonTags(e.target.value)}
                    placeholder="Tags (comma-separated)"
                    className="w-full text-sm bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] rounded px-3 py-2 text-[var(--text-primary)]"
                    data-testid="lesson-tags-input"
                  />
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-[var(--text-secondary)] whitespace-nowrap">
                      Auto-bank isomorphs:
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="72"
                      value={lessonIsomorphCount}
                      onChange={(e) => setLessonIsomorphCount(e.target.value)}
                      className="w-20 text-sm bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] rounded px-3 py-2 text-[var(--text-primary)]"
                      data-testid="lesson-isomorph-count"
                    />
                    <span className="text-xs text-[var(--text-disabled)]">
                      cosmetic variants saved to play cache (0–72)
                    </span>
                  </div>
                </div>
              </div>
              <textarea
                value={lessonDescription}
                onChange={(e) => setLessonDescription(e.target.value)}
                placeholder="Description / show notes (markdown OK, plain newlines preserved)"
                rows={3}
                className="w-full text-sm bg-[var(--hover-bg-subtle)] border border-[var(--border-subtle)] rounded p-3 text-[var(--text-primary)]"
                data-testid="lesson-description-input"
              />
              <div className="flex justify-end">
                <Button
                  onClick={createLesson}
                  disabled={busy.lesson || !lessonGrid.trim() || !lessonTitle.trim()}
                  data-testid="lesson-create-btn"
                  className="bg-[#FF3B30] hover:bg-[#FF6961] text-white"
                >
                  {busy.lesson ? 'Saving…' : 'Create Lesson'}
                </Button>
              </div>

              {lessons.length > 0 && (
                <div className="border-t border-[var(--border-subtle)] pt-3">
                  <h4 className="text-sm font-semibold text-[var(--text-secondary)] mb-2">
                    Published lessons ({lessons.length})
                  </h4>
                  <ul className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="lessons-list">
                    {lessons.map((l) => (
                      <li
                        key={l.code}
                        className="flex items-center justify-between gap-3 text-sm bg-[var(--hover-bg-subtle)] rounded px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <code className="font-mono text-xs text-[#FF3B30]">{l.code}</code>
                            <span className="text-[var(--text-disabled)] text-xs">L{l.difficulty_level}</span>
                            {l.video_id && <span className="text-[var(--text-disabled)] text-xs">▶ {l.video_id}</span>}
                          </div>
                          <div className="text-[var(--text-primary)] truncate">{l.title}</div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/lessons/${l.code}`)}
                          className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteLesson(l.code)}
                          className="text-[#FF3B30] hover:text-[#FF6961]"
                          data-testid={`lesson-delete-${l.code}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Weekly digest card — spans full width */}
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] md:col-span-2" data-testid="card-digest">
            <CardHeader>
              <CardTitle className="text-[var(--text-primary)] flex items-center gap-2 font-heading">
                <Mail className="w-4 h-4 text-[#FF9F0A]" />
                Weekly Digest
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5 text-sm">
                <DigestStat
                  icon={Clock}
                  label="Next auto-send"
                  value={digestStatus?.next_auto_send_at
                    ? new Date(digestStatus.next_auto_send_at).toLocaleString(undefined, {
                        weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })
                    : '—'}
                />
                <DigestStat
                  icon={Gauge}
                  label="Last sent"
                  value={digestStatus?.last_sent_at
                    ? new Date(digestStatus.last_sent_at).toLocaleString(undefined, {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })
                    : 'Never'}
                />
                <DigestStat
                  icon={Send}
                  label="Last count"
                  value={digestStatus?.last_sent_count ?? '—'}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={previewDigest}
                  disabled={busy.preview}
                  className="border-[var(--border-subtle)]"
                  data-testid="digest-preview-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {busy.preview ? 'Loading…' : 'Preview'}
                </Button>
                <Button
                  onClick={sendDigest}
                  disabled={busy.send || !newsletter?.count}
                  className="bg-[#FF9F0A] hover:bg-[#FFB340] text-white"
                  data-testid="digest-send-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {busy.send ? 'Sending…' : `Send now (${newsletter?.count ?? 0})`}
                </Button>
              </div>

              {/* Auto-scheduled note */}
              <div className="mt-4 text-xs text-[var(--text-disabled)]">
                Auto-sends every Monday at 09:00 UTC. Manual sends enforce a 3-day
                cooldown to prevent accidental double-sends.
              </div>

              {/* Preview output */}
              {previewHtml && (
                <div className="mt-6 border-t border-[var(--border-subtle)] pt-4">
                  <h4 className="font-heading font-semibold text-[var(--text-primary)] mb-3">
                    Preview
                  </h4>
                  {previewSummary && (
                    <div className="text-xs text-[var(--text-secondary)] font-mono mb-3 bg-[var(--hover-bg-subtle)] p-3 rounded">
                      {JSON.stringify(previewSummary, null, 2)}
                    </div>
                  )}
                  <div
                    className="bg-white rounded border border-[var(--border-subtle)] overflow-hidden"
                    data-testid="digest-preview-frame"
                  >
                    <iframe
                      title="Digest preview"
                      srcDoc={previewHtml}
                      style={{ width: '100%', minHeight: 500, border: 0 }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      {pendingCropFile && (
        <PuzzleCropModal
          imageFile={pendingCropFile}
          onCancel={() => setPendingCropFile(null)}
          onConfirm={handleCroppedBlob}
        />
      )}
    </div>
  );
}

function DigestStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-[var(--hover-bg-subtle)] rounded p-3">
      <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)] mb-1">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-sm font-medium text-[var(--text-primary)] truncate">
        {value}
      </div>
    </div>
  );
}
